<template>
  <div class="sales-report-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-chart-line me-2 text-primary"></i>
            <strong>Sales Report</strong>
          </h4>
          <small class="text-muted">View and update sales transaction details, costs, and financial information</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading sales report...</p>
      </div>
    </div>

    <!-- Payment Buckets card — shows the latest live sums from the four
         payment buckets that were added in recent feature work. These are
         the system-of-record values for deposits/payments now; the legacy
         flat fields below stay synced but admins should treat the bucket
         totals as canonical. -->
    <CRow v-if="!loading" class="mb-3">
      <CCol sm="12">
        <CCard class="payment-buckets-card">
          <CCardHeader class="bucket-card-header">
            <i class="fas fa-coins me-2"></i>
            <strong>Latest Payment Buckets</strong>
            <small class="text-muted ms-2">Live sums from the new per-tab payment buckets</small>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="3">
                <div class="bucket-tile tile-success">
                  <div class="bucket-label">Customer Payments (Deposits)</div>
                  <div class="bucket-value">RM {{ formatMoney(totalCustomerPayments) }}</div>
                  <small class="bucket-source">
                    <i class="fas fa-info-circle me-1"></i>
                    Σ Sale › Customer Payments ({{ salePaymentsCount }} payment{{ salePaymentsCount === 1 ? '' : 's' }})
                  </small>
                </div>
              </CCol>
              <CCol md="3">
                <div class="bucket-tile tile-info">
                  <div class="bucket-label">Purchase Payments</div>
                  <div class="bucket-value">RM {{ formatMoney(totalPurchasePayments) }}</div>
                  <small class="bucket-source">
                    <i class="fas fa-info-circle me-1"></i>
                    Σ Purchase › Payments ({{ purchasePaymentsCount }} payment{{ purchasePaymentsCount === 1 ? '' : 's' }})
                  </small>
                </div>
              </CCol>
              <CCol md="3">
                <div class="bucket-tile tile-warning">
                  <div class="bucket-label">Expense Payments</div>
                  <div class="bucket-value">RM {{ formatMoney(totalExpensePayments) }}</div>
                  <small class="bucket-source">
                    <i class="fas fa-info-circle me-1"></i>
                    Σ Expenses › Payments ({{ expensePaymentsCount }} payment{{ expensePaymentsCount === 1 ? '' : 's' }})
                  </small>
                </div>
              </CCol>
              <CCol md="3">
                <div class="bucket-tile tile-secondary">
                  <div class="bucket-label">Admin Cost Payments</div>
                  <div class="bucket-value">RM {{ formatMoney(totalAdminCostPayments) }}</div>
                  <small class="bucket-source">
                    <i class="fas fa-info-circle me-1"></i>
                    Σ Admin Cost › Payments ({{ adminCostPaymentsCount }} payment{{ adminCostPaymentsCount === 1 ? '' : 's' }})
                  </small>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Sales Report Form -->
    <CRow v-if="!loading">
      <CCol sm="12">
        <CCard class="form-card">
          <CCardHeader class="form-header">
            <div class="d-flex align-items-center">
              <i class="fas fa-file-invoice-dollar me-2 text-primary"></i>
              <strong>Sales Transaction Details</strong>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Vehicle Information Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-car me-2 text-primary"></i>
                    <strong>Vehicle Information</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Stock No</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.stockNo"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-hashtag me-1"></i>
                    Stock reference number
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Brand</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    :value="computedBrandName"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-trademark me-1"></i>
                    Vehicle brand name
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Chassis No</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="chasisNo"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-barcode me-1"></i>
                    Vehicle chassis number
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Registration No</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="vehicleRegistrationNumber"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-id-card me-1"></i>
                    Vehicle registration plate number
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Registration Date</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="vehicleRegstrationDate"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-calendar me-1"></i>
                    Date of vehicle registration
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Customer & Salesman Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-users me-2 text-primary"></i>
                    <strong>Customer & Salesman Information</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Customer</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    :value="customerName"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-user me-1"></i>
                    Customer name
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Salesman</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="salesmanName"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-user-tie me-1"></i>
                    Salesperson name
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Sales Information Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-dollar-sign me-2 text-primary"></i>
                    <strong>Sales Information</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Body Price</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.purchase.bodyPriceLocalCurrency"
                    prepend="RM"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-money-bill me-1"></i>
                    Vehicle body price in local currency
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Selling Price</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.sale.saleAmount"
                    prepend="RM"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-tag me-1"></i>
                    Final selling price to customer
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>HP Amount</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.sale.hpAmount"
                    prepend="RM"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-handshake me-1"></i>
                    Hire purchase amount
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Purchase Cost Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-shopping-cart me-2 text-primary"></i>
                    <strong>Purchase Cost</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Supplier Currency</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.purchase.supplierCurrency"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-yen-sign me-1"></i>
                    Currency code (e.g., JPY, USD)
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Purchase Price (Foreign)</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.purchase.vehiclePriceSupplierCurrency"
                    :prepend="obj.purchase.supplierCurrency"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-globe me-1"></i>
                    Purchase price in foreign currency
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Purchase Price (Local)</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.purchase.vehiclePriceLocalCurrency"
                    prepend="RM"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-exchange-alt me-1"></i>
                    Purchase price in local currency (TT)
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Administrative Costs Section — read-only category
                   breakdown sourced from the AdministrativeCostPayments
                   bucket. Categories with no bucket (Insurance / Reg Fee
                   / Road Tax) fall back to the legacy flat field. Admins
                   edit the values on the Stock › Administrative Cost tab. -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-file-invoice me-2 text-primary"></i>
                    <strong>Administrative &amp; Processing Costs</strong>
                    <small class="text-muted ms-2 fw-normal">
                      Live sums from the Admin Cost payment bucket
                      <CButton size="sm" color="link" class="p-0 ms-2" @click="openStockTab(obj.id)">
                        <i class="fas fa-external-link-alt me-1"></i>Edit on Stock tab
                      </CButton>
                    </small>
                  </h6>
                </CCol>
              </CRow>

              <div class="category-summary">
                <CategoryRow
                  label="Duty Tax"
                  icon="fas fa-receipt"
                  :amount="adminCostByType.DutyTax.sum"
                  :count="adminCostByType.DutyTax.count"
                  source="Σ Admin Cost › Payments (CostType=DutyTax)"
                />
                <CategoryRow
                  label="AP Amount"
                  icon="fas fa-stamp"
                  :amount="adminCostByType.Ap.sum"
                  :count="adminCostByType.Ap.count"
                  source="Σ Admin Cost › Payments (CostType=Ap)"
                />
                <CategoryRow
                  label="Forwarding"
                  icon="fas fa-shipping-fast"
                  :amount="adminCostByType.Forwarding.sum"
                  :count="adminCostByType.Forwarding.count"
                  source="Σ Admin Cost › Payments (CostType=Forwarding)"
                />
                <CategoryRow
                  label="Int FS"
                  icon="fas fa-ship"
                  :amount="adminCostByType.IntFs.sum"
                  :count="adminCostByType.IntFs.count"
                  source="Σ Admin Cost › Payments (CostType=IntFs)"
                />
                <CategoryRow
                  label="Puspakom"
                  icon="fas fa-clipboard-check"
                  :amount="adminCostByType.Puspakom.sum"
                  :count="adminCostByType.Puspakom.count"
                  source="Σ Admin Cost › Payments (CostType=Puspakom)"
                />
                <CategoryRow
                  label="Transfer Fee"
                  icon="fas fa-exchange-alt"
                  :amount="adminCostByType.TransferFee.sum"
                  :count="adminCostByType.TransferFee.count"
                  source="Σ Admin Cost › Payments (CostType=TransferFee)"
                />
                <CategoryRow
                  v-if="adminCostByType.Others.count > 0"
                  label="Others"
                  icon="fas fa-ellipsis-h"
                  :amount="adminCostByType.Others.sum"
                  :count="adminCostByType.Others.count"
                  source="Σ Admin Cost › Payments (CostType=Others)"
                />

                <!-- Flat-field categories (no bucket counterpart yet) -->
                <CategoryRow
                  label="Insurance"
                  icon="fas fa-shield-alt"
                  :amount="(obj.adminitrativeCost && obj.adminitrativeCost.insuranceAmount) || 0"
                  source="Administrative Cost › Insurance Amount (flat field)"
                  :isLegacy="true"
                />
                <CategoryRow
                  label="Registration Fee"
                  icon="fas fa-id-badge"
                  :amount="(obj.adminitrativeCost && obj.adminitrativeCost.registrationFeeAmount) || 0"
                  source="Administrative Cost › Registration Fee Amount (flat field)"
                  :isLegacy="true"
                />
                <CategoryRow
                  label="Road Tax"
                  icon="fas fa-road"
                  :amount="(obj.adminitrativeCost && obj.adminitrativeCost.roadtaxAmount) || 0"
                  source="Administrative Cost › Roadtax Amount (flat field)"
                  :isLegacy="true"
                />

                <div class="category-total">
                  <span class="cat-total-label">TOTAL ADMIN COSTS</span>
                  <span class="cat-total-value">RM {{ formatMoney(adminCostGrandTotal) }}</span>
                </div>
              </div>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Other Expenses Section — read-only category breakdown
                   sourced from the ExpensePayments bucket. Admins edit
                   values on the Stock › Expenses tab. -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-tools me-2 text-primary"></i>
                    <strong>Other Expenses</strong>
                    <small class="text-muted ms-2 fw-normal">
                      Live sums from the Expenses payment bucket
                      <CButton size="sm" color="link" class="p-0 ms-2" @click="openStockTab(obj.id)">
                        <i class="fas fa-external-link-alt me-1"></i>Edit on Stock tab
                      </CButton>
                    </small>
                  </h6>
                </CCol>
              </CRow>

              <div class="category-summary">
                <CategoryRow
                  label="Service EzCare"
                  icon="fas fa-wrench"
                  :amount="expenseByType.ServiceEzCare.sum"
                  :count="expenseByType.ServiceEzCare.count"
                  source="Σ Expenses › Payments (ExpenseType=ServiceEzCare)"
                />
                <CategoryRow
                  label="Interior"
                  icon="fas fa-couch"
                  :amount="expenseByType.Interior.sum"
                  :count="expenseByType.Interior.count"
                  source="Σ Expenses › Payments (ExpenseType=Interior)"
                />
                <CategoryRow
                  label="Paint"
                  icon="fas fa-paint-roller"
                  :amount="expenseByType.Paint.sum"
                  :count="expenseByType.Paint.count"
                  source="Σ Expenses › Payments (ExpenseType=Paint)"
                />
                <CategoryRow
                  label="Tyre"
                  icon="fas fa-circle"
                  :amount="expenseByType.Tyre.sum"
                  :count="expenseByType.Tyre.count"
                  source="Σ Expenses › Payments (ExpenseType=Tyre)"
                />
                <CategoryRow
                  label="Rental"
                  icon="fas fa-warehouse"
                  :amount="expenseByType.Rental.sum"
                  :count="expenseByType.Rental.count"
                  source="Σ Expenses › Payments (ExpenseType=Rental)"
                />
                <CategoryRow
                  v-if="expenseByType.Others.count > 0"
                  label="Others"
                  icon="fas fa-ellipsis-h"
                  :amount="expenseByType.Others.sum"
                  :count="expenseByType.Others.count"
                  source="Σ Expenses › Payments (ExpenseType=Others)"
                />

                <div class="category-total">
                  <span class="cat-total-label">TOTAL OTHER EXPENSES</span>
                  <span class="cat-total-value">RM {{ formatMoney(expenseGrandTotal) }}</span>
                </div>
              </div>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Sales Costs Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-percentage me-2 text-primary"></i>
                    <strong>Sales Costs</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Salesman Commission</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.sale.salesmanCommisionAmount"
                    prepend="RM"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-percent me-1"></i>
                    Commission paid to salesperson
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Promotion Discount</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="obj.sale.promotionDiscountAmount"
                    prepend="RM"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-badge-percent me-1"></i>
                    Promotional discount amount
                  </small>
                </CCol>
              </CRow>

              <!-- Divider -->
              <hr class="my-4" />

              <!-- Loan Information Section -->
              <CRow class="mb-3">
                <CCol>
                  <h6 class="section-header">
                    <i class="fas fa-university me-2 text-primary"></i>
                    <strong>Loan Information</strong>
                  </h6>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Bank</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="bankLoanName"
                    readonly
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-university me-1"></i>
                    Financing bank name
                  </small>
                </CCol>
              </CRow>

              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Loan Amount</strong>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="bankLoanAmount"
                    prepend="RM"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-money-check me-1"></i>
                    Approved loan amount
                  </small>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>

          <!-- Form Footer -->
          <CCardFooter class="form-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <CButton
                  color="primary"
                  :disabled="submitting"
                  @click="onSubmit"
                >
                  <CSpinner v-if="submitting" size="sm" class="me-2" />
                  <i v-else class="fas fa-check-circle me-2"></i>
                  {{ submitting ? 'Updating...' : 'Submit' }}
                </CButton>
                <CButton
                  color="secondary"
                  class="ms-2"
                  @click="cancel"
                  :disabled="submitting"
                >
                  <i class="fas fa-times me-2"></i>
                  Cancel
                </CButton>
              </div>
              <small class="text-muted">
                <i class="fas fa-info-circle me-1"></i>
                All amounts in RM unless specified otherwise
              </small>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import StockApi from "../../lib/stockApi";
import moment from "moment";
import { formatMalaysiaDate } from "@/utils/dateUtil";

// Inline category-row component: renders one line of the Admin Costs /
// Other Expenses breakdown. Each row shows category label, amount (right-
// aligned monospaced) and a source annotation. Rows with `isLegacy` get
// a different background tint to indicate they're flat-field-only (no
// payment bucket yet).
const CategoryRow = {
  name: "CategoryRow",
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: "fas fa-circle" },
    amount: { type: [Number, String], default: 0 },
    count: { type: Number, default: null },
    source: { type: String, default: "" },
    isLegacy: { type: Boolean, default: false },
  },
  computed: {
    formattedAmount() {
      const v = Number(this.amount) || 0;
      return v.toLocaleString("en-MY", {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      });
    },
  },
  template: `
    <div class="cat-row" :class="{ 'cat-legacy': isLegacy }">
      <div class="cat-label">
        <i :class="icon" class="me-2"></i>
        <span>{{ label }}</span>
      </div>
      <div class="cat-amount">RM {{ formattedAmount }}</div>
      <div class="cat-source">
        <small><i class="fas fa-arrow-right me-1"></i>
          <span v-if="count != null && count > 0">{{ count }} payment{{ count === 1 ? '' : 's' }}</span>
          <span v-else-if="count === 0" class="text-muted fst-italic">no payments yet</span>
          <span v-else>{{ source }}</span>
        </small>
      </div>
    </div>
  `,
};

export default {
  name: "SalesReport",
  components: { CategoryRow },
  data() {
    return {
      infoList: [],
      api: new StockApi(),
      bankLoanName: "",
      bankLoanAmount: 0,
      obj: {
        stockNo: "",
        vehicle: {},
        registration: {},
        purchase: {
          supplierCurrency: "",
          vehiclePriceSupplierCurrency: 0,
          vehiclePriceLocalCurrency: 0,
          bodyPriceLocalCurrency: 0,
        },
        sale: {
          saleAmount: 0,
          hpAmount: 0,
          salesmanCommisionAmount: 0,
          promotionDiscountAmount: 0,
          salesmanName: "",
          customer: {},
          loan: {},
        },
        adminitrativeCost: {
          dutyCostAmount: 0,
          apCostAmount: 0,
          forwardingCostAmount: 0,
          intFsCostAmount: 0,
          insuranceAmount: 0,
          puspakomAmount: 0,
          transferFeeAmount: 0,
          registrationFeeAmount: 0,
          roadtaxAmount: 0,
        },
        expense: {
          serviceEzCareCostAmount: 0,
          interiorCostAmount: 0,
          paintCostAmount: 0,
          tyreCostAmount: 0,
          rentalCostAmount: 0,
        },
      },
      loading: false,
      submitting: false,
    };
  },
  mounted() {
    this.resetObj();
  },
  computed: {
    computedBrandName() {
      return this.getBrandName();
    },
    chasisNo() {
      try {
        return this.obj.vehicle.chasisNo;
      } catch (error) {
        return "N/A";
      }
    },
    vehicleRegistrationNumber() {
      try {
        return this.obj.registration.vehicleRegistrationNumber;
      } catch (error) {
        return "N/A";
      }
    },
    salesmanName() {
      try {
        return this.obj.sale.salesmanName;
      } catch (error) {
        return "N/A";
      }
    },
    customerName() {
      try {
        return this.obj.sale.customer.name;
      } catch (error) {
        return "N/A";
      }
    },
    vehicleRegstrationDate() {
      if (
        this.obj &&
        this.obj.registration &&
        this.obj.registration.vehicleRegistrationDate
      )
        return this.getDisplayDate(
          this.obj.registration.vehicleRegistrationDate
        );
      return "N/A";
    },

    // ----- Live payment-bucket sums from the four recently-added buckets.
    //       These are the system-of-record values for payments now.
    salePayments() {
      return (this.obj && this.obj.sale && this.obj.sale.salePayments) || [];
    },
    purchasePayments() {
      return (this.obj && this.obj.purchase && this.obj.purchase.purchasePayments) || [];
    },
    expensePayments() {
      return (this.obj && this.obj.expense && this.obj.expense.expensePayments) || [];
    },
    adminCostPayments() {
      return (this.obj && this.obj.adminitrativeCost && this.obj.adminitrativeCost.adminitrativeCostPayments) || [];
    },
    totalCustomerPayments() {
      return this.salePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    totalPurchasePayments() {
      return this.purchasePayments.reduce((s, p) => s + (Number(p.localAmount) || 0), 0);
    },
    totalExpensePayments() {
      return this.expensePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    totalAdminCostPayments() {
      return this.adminCostPayments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    },
    salePaymentsCount() { return this.salePayments.length; },
    purchasePaymentsCount() { return this.purchasePayments.length; },
    expensePaymentsCount() { return this.expensePayments.length; },
    adminCostPaymentsCount() { return this.adminCostPayments.length; },

    // ----- Per-category breakdown for Administrative Cost ---------------
    //       Reads the bucket payments and groups them by CostType. The
    //       categories that have no bucket counterpart (Insurance /
    //       RegistrationFee / RoadTax) keep reading their legacy flat
    //       column from obj.adminitrativeCost.
    adminCostByType() {
      const m = { DutyTax: { sum: 0, count: 0 }, Ap: { sum: 0, count: 0 },
                  Forwarding: { sum: 0, count: 0 }, IntFs: { sum: 0, count: 0 },
                  Puspakom: { sum: 0, count: 0 }, TransferFee: { sum: 0, count: 0 },
                  Others: { sum: 0, count: 0 } };
      for (const p of this.adminCostPayments) {
        const key = p.costType || "Others";
        if (m[key]) { m[key].sum += Number(p.amount) || 0; m[key].count += 1; }
        else { m.Others.sum += Number(p.amount) || 0; m.Others.count += 1; }
      }
      return m;
    },

    // ----- Per-category breakdown for Expense (Other Expenses) ----------
    expenseByType() {
      const m = { ServiceEzCare: { sum: 0, count: 0 }, Interior: { sum: 0, count: 0 },
                  Paint: { sum: 0, count: 0 }, Tyre: { sum: 0, count: 0 },
                  Rental: { sum: 0, count: 0 }, Others: { sum: 0, count: 0 } };
      for (const p of this.expensePayments) {
        const key = p.expenseType || "Others";
        if (m[key]) { m[key].sum += Number(p.amount) || 0; m[key].count += 1; }
        else { m.Others.sum += Number(p.amount) || 0; m.Others.count += 1; }
      }
      return m;
    },

    // ----- Grand-total for the Admin Cost section (bucket-only categories
    //       plus the three legacy-only categories: Insurance / RegFee / RoadTax)
    adminCostGrandTotal() {
      const b = this.adminCostByType;
      const bucketTotal = b.DutyTax.sum + b.Ap.sum + b.Forwarding.sum +
                          b.IntFs.sum + b.Puspakom.sum + b.TransferFee.sum + b.Others.sum;
      const ac = this.obj.adminitrativeCost || {};
      return bucketTotal +
        (Number(ac.insuranceAmount) || 0) +
        (Number(ac.registrationFeeAmount) || 0) +
        (Number(ac.roadtaxAmount) || 0);
    },
    expenseGrandTotal() {
      const e = this.expenseByType;
      return e.ServiceEzCare.sum + e.Interior.sum + e.Paint.sum +
             e.Tyre.sum + e.Rental.sum + e.Others.sum;
    },
  },
  methods: {
    getBrandName() {
      try {
        return this.obj.vehicle.brand.name;
      } catch (error) {
        return "N/A";
      }
    },

    getDisplayDate(item) {
      return formatMalaysiaDate(item, 'YYYY/MM/DD');
    },
    openStockTab(stockId) {
      if (!stockId) return;
      // Open the Stock detail page in a new tab so the user keeps the
      // report context. The Stock page tabs are addressable via the hash.
      const url = this.$router.resolve({ name: "stockById", params: { id: stockId } });
      if (url && url.href) window.open(url.href, "_blank");
    },
    formatMoney(n) {
      const v = Number(n) || 0;
      return v.toLocaleString("en-MY", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    onSubmit() {
      this.submitting = true;
      this.obj.sale.loan.approvedLoanAmount = this.bankLoanAmount;

      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.toast("Success", "Sales report created successfully", "success");
            setTimeout(() => {
              this.resetObj();
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then((response) => {
            this.toast("Success", "Sales report updated successfully", "success");
            setTimeout(() => {
              this.resetObj();
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      }
    },

    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    resetObj() {
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.bankLoanName = this.getBankLoanName(this.obj);
            this.bankLoanAmount = this.getBankLoanAmount(this.obj);
            this.loading = false;
            this.submitting = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
            this.submitting = false;
          });
      } else {
        this.loading = false;
        this.submitting = false;
      }
    },

    getBankLoanAmount(item) {
      try {
        return item.sale.loan.approvedLoanAmount;
      } catch (error) {
        return 0;
      }
    },

    getBankLoanName(item) {
      try {
        return item.sale.loan.bank.name;
      } catch (error) {
        return "N/A";
      }
    },

    cancel() {
      this.$router.push({ path: "/admins/SalesReportList" });
    },
  },
};
</script>

<style scoped>
/* ===== Sales Report — unified typography ===== */
/* This block mirrors SalesReportList.vue so both pages use the same
   fonts, sizes, weights, and color tokens. Keep them in sync. */
.sales-report-wrapper {
  padding: 1rem;
  background: #f3f4f7;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 0.875rem;
  color: #2d3748;
  line-height: 1.5;
}

.sales-report-wrapper h4 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1c2742;
  margin: 0;
  letter-spacing: -0.2px;
}

.sales-report-wrapper small,
.sales-report-wrapper .form-text {
  font-size: 0.8rem;
  color: #768192;
}

/* ===== Payment Buckets card ===== */
.payment-buckets-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.bucket-card-header {
  background: #f8f9fa;
  border-bottom: 2px solid #321fdb;
  color: #321fdb;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
}
.bucket-card-header strong { font-size: 0.95rem; font-weight: 700; }
.bucket-card-header small { font-weight: 400; color: #768192; }

.bucket-tile {
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid #c0c5d1;
  background: #f8f9fa;
  margin-bottom: 0.5rem;
  min-height: 110px;
}
.bucket-tile.tile-success { border-left-color: #2eb85c; background: #ebf8ef; }
.bucket-tile.tile-info { border-left-color: #39f; background: #e7f1ff; }
.bucket-tile.tile-warning { border-left-color: #f9b115; background: #fff6e1; }
.bucket-tile.tile-secondary { border-left-color: #4f5d73; background: #f1f3f7; }
.bucket-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #4f5d73;
  font-weight: 600;
  margin-bottom: 0.4rem;
}
.bucket-value {
  font-family: "Courier New", Consolas, monospace;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1c2742;
  margin-bottom: 0.3rem;
}
.bucket-source {
  display: block;
  font-size: 0.72rem;
  color: #768192;
  font-style: italic;
}

/* ===== Toast Notifications ===== */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* ===== Page Header ===== */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
}

/* ===== Loading State ===== */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== Form Card ===== */
.form-card {
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sales-report-wrapper .form-card .form-header {
  background: #f8f9fa;
  border-bottom: 2px solid #321fdb;
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: #321fdb;
}

.sales-report-wrapper .form-card .form-header strong,
.sales-report-wrapper .form-card .form-header i {
  font-size: 0.95rem;
  font-weight: 700;
  color: #321fdb !important;
}

/* ===== Form Body ===== */
.form-card .card-body {
  padding: 1.5rem 1.25rem;
}

/* ===== Form Labels ===== */
.col-form-label,
.sales-report-wrapper >>> label {
  font-weight: 600;
  font-size: 0.85rem;
  color: #4f5d73;
}

.sales-report-wrapper >>> .form-control {
  font-family: inherit;
  font-size: 0.85rem;
}

/* ===== Sub-section divider header (inside the form) ===== */
.section-header {
  margin: 1rem 0 0.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--cui-border-color, #e9ecef);
  font-size: 0.95rem;
  font-weight: 700;
  color: #321fdb;
}

/* ===== Category summary (Admin Costs / Other Expenses) ===== */
.category-summary {
  background: #fafbfc;
  border: 1px solid #e4e7ea;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  margin: 0.5rem 0 1rem 0;
}

.cat-row {
  display: grid;
  grid-template-columns: 32% 22% 46%;
  gap: 0.75rem;
  align-items: center;
  padding: 0.45rem 0;
  border-bottom: 1px dashed #e4e7ea;
}
.cat-row:last-of-type { border-bottom: none; }

.cat-row.cat-legacy {
  background: #fff8e7;
  border-left: 3px solid #f9b115;
  padding-left: 0.5rem;
  border-radius: 3px;
}

.cat-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4f5d73;
}

.cat-label i {
  color: #321fdb;
  width: 1.1rem;
  text-align: center;
}

.cat-amount {
  font-family: "Courier New", Consolas, monospace;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1c2742;
  text-align: right;
}

.cat-source {
  font-size: 0.72rem;
  color: #768192;
}

.cat-source i {
  color: #768192;
}

.category-total {
  display: grid;
  grid-template-columns: 54% 46%;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0.5rem;
  margin-top: 0.4rem;
  background: #e8f7ed;
  border-left: 4px solid #2eb85c;
  border-radius: 3px;
}

.cat-total-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1c2742;
  letter-spacing: 0.3px;
}

.cat-total-value {
  font-family: "Courier New", Consolas, monospace;
  font-size: 1rem;
  font-weight: 700;
  color: #1c2742;
  text-align: right;
}

/* Divider */
hr {
  border-top: 1px solid var(--cui-border-color, #dee2e6);
}

/* Form Footer */
.form-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .sales-report-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .form-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .form-footer {
    display: none !important;
  }

  .form-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }

  .section-header {
    page-break-after: avoid;
  }
}
</style>
